import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';

import { PipelineEditorEffects } from '../../../effects/pipeline-editor.effects';
import { SaveProtocolCascadeEffects } from '../../../effects/save-protocol-cascade.effects';
import { PipelineEditorState } from '../../../state/pipeline-editor.state';

/**
 * Persist-now consumer for the standalone route host (`PipelineEditorPageComponent`).
 *
 * `PipelineEditorComponent` is a pure draft producer — its `save`/`cancel` emit
 * a one-shot `PipelineEditorEffects.outcome$` event and never persist. This
 * facade owns the route-side decision: on `saved` it runs the protocol cascade
 * (the cascade reads the shared `PipelineEditorState.draft$` itself), and it
 * exposes the resulting navigation intents (`saved$`, `cancelled$`) for the thin
 * host to react to. Keeping this orchestration in a facade — not the component —
 * follows the UI-layer rule that components hold no business logic.
 *
 * The method-type dialog consumer reuses the same producer with a *different*
 * decision (stage the draft for the deferred method-type save) — that lives in
 * its own consumer, not here.
 */
@Injectable()
export class PipelineEditorPageFacade {
  constructor(
    private readonly editorEffects: PipelineEditorEffects,
    private readonly state: PipelineEditorState,
    private readonly cascadeEffects: SaveProtocolCascadeEffects
  ) {}

  /**
   * Seeds the editor (create mode when `pipelineId` is null) and wires the
   * persist-now reaction: every `saved` outcome triggers the cascade. Built on
   * `state.effect` so the ongoing subscription's teardown follows the per-page
   * state lifecycle, and a retry after a save error re-triggers naturally.
   */
  public readonly initialize = this.state.effect((pipelineId$: Observable<string | null>) =>
    pipelineId$.pipe(
      tap((pipelineId) => this.editorEffects.loadPipeline(pipelineId)),
      switchMap(() => this.editorEffects.outcome$),
      filter((outcome) => outcome.kind === 'saved'),
      tap(() => this.cascadeEffects.consolidatePipeline())
    )
  );

  /** Navigation intent: the cascade finished a full pipeline-rooted save. */
  public readonly saved$: Observable<void> = this.cascadeEffects.cascadeCompleted$;

  /** Navigation intent: the user cancelled the edit. */
  public readonly cancelled$: Observable<void> = this.editorEffects.outcome$.pipe(
    filter((outcome) => outcome.kind === 'cancelled'),
    map(() => undefined)
  );
}
